/**
 * Request-shape schemas for the money route family (bills, reimbursements,
 * loans, cash requests, reimbursement receipts).
 *
 * These used to live inline in apps/api/src/routes/money.ts, invisible to
 * the strictness sweep (strictness.test.ts). Moved here verbatim — shapes
 * are unchanged; only the export location moved.
 */
import { z } from 'zod';
import { CashRequestState, LoanState, ReceiptOcrStatus, ReimbursementState } from './money.js';
import {
  CashRequestId,
  ExpectedBillId,
  LoanId,
  ReceiptId,
  ReimbursementId,
  UserId,
} from './primitives.js';

// === Expected bills ===
export const CreateExpectedBillBody = z.object({
  label: z.string().min(1).max(120),
  amount_minor: z.number().int().min(0),
  currency: z.string().length(3),
  next_due_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD'),
  cadence: z.enum(['once', 'monthly', 'quarterly', 'annual']).optional(),
  category: z.string().max(60).optional(),
  notes: z.string().max(500).optional(),
});
export type CreateExpectedBillBody = z.infer<typeof CreateExpectedBillBody>;
export const UpdateExpectedBillBody = CreateExpectedBillBody.partial();

export const ListExpectedBillsQuery = z.object({ include_paid: z.coerce.boolean().optional() });
export const BillsDueSoonQuery = z.object({
  days: z.coerce.number().int().min(1).max(365).optional(),
});
export const ExpectedBillIdParams = z.object({ id: ExpectedBillId });

// === Reimbursements ===
export const ReimbursementDecisionPath = z.enum(['approve', 'reject', 'paid']);
export const ReimbursementDecisionBody = z.object({
  note: z.string().max(500).optional(),
  paid_via: z.string().max(120).optional(),
});
export const ListReimbursementsQuery = z.object({
  state: ReimbursementState.optional(),
  staff_user_id: UserId.optional(),
});
export const ReimbursementIdParams = z.object({ id: ReimbursementId });
export const ReimbursementDecisionParams = z.object({
  id: ReimbursementId,
  decision: ReimbursementDecisionPath,
});

// === Loans ===
export const LoanWriteOffBody = z.object({ note: z.string().min(1).max(500) });
export const ListLoansQuery = z.object({
  staff_user_id: UserId.optional(),
  state: LoanState.optional(),
});
export const LoanIdParams = z.object({ id: LoanId });

// === Cash requests ===
export const CashRequestDecisionPath = z.enum(['approve', 'reject', 'dispense', 'reconcile']);
export const CashRequestDecisionBody = z.object({
  note: z.string().max(500).optional(),
  reconciled_receipt_id: ReceiptId.optional(),
});
export const ListCashRequestsQuery = z.object({
  state: CashRequestState.optional(),
  requested_by: UserId.optional(),
});
export const CashRequestDecisionParams = z.object({
  id: CashRequestId,
  decision: CashRequestDecisionPath,
});

// === Reimbursement receipts ===
export const ReceiptDecisionPath = z.enum(['approve', 'reject']);
export const ListReimbursementReceiptsQuery = z.object({
  status: ReceiptOcrStatus.optional(),
  uploaded_by: UserId.optional(),
});
export const ReceiptDecisionParams = z.object({
  id: ReceiptId,
  decision: ReceiptDecisionPath,
});
